import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://ufc-backend-nmib.onrender.com/api';

export default function WinProbability({ prediction, fighter1, fighter2 }) {
  const [odds,        setOdds]        = useState(null);
  const [oddsLoading, setOddsLoading] = useState(false);

  useEffect(() => {
    if (!fighter1?.full_name || !fighter2?.full_name) return;
    setOdds(null);
    setOddsLoading(true);
    axios.get(`${API}/matchup/odds`, {
      params: { f1: fighter1.full_name, f2: fighter2.full_name }
    })
      .then(r => setOdds(r.data))
      .catch(() => setOdds({ available: false }))
      .finally(() => setOddsLoading(false));
  }, [fighter1?.full_name, fighter2?.full_name]);

  if (!prediction || prediction.error) {
    return (
      <div className="card">
        <h2 className="section-title">WIN PROBABILITY</h2>
        <div style={{ color: 'var(--text-dim)', fontSize: 14 }}>
          {prediction?.error || 'Not enough data to generate a prediction.'}
        </div>
      </div>
    );
  }

  const f1Pct   = Math.round(prediction.f1_win_prob * 100);
  const f2Pct   = Math.round(prediction.f2_win_prob * 100);
  const hasOdds = odds?.available;

  // Compute edge — only show on the fighter where model > market
  let f1Edge = null, f2Edge = null;
  if (hasOdds) {
    const f1Implied = Math.round(odds.f1_implied * 100);
    const f2Implied = Math.round(odds.f2_implied * 100);
    const diff1 = f1Pct - f1Implied;
    const diff2 = f2Pct - f2Implied;
    // Only show on the side with positive edge (model more bullish than market)
    if (diff1 >= 2)  f1Edge = diff1;
    if (diff2 >= 2)  f2Edge = diff2;
  }

  return (
    <div className="card">
      <h2 className="section-title">WIN PROBABILITY</h2>

      {/* Fighter names */}
      <div className="prob-names">
        <span>{fighter1?.full_name}</span>
        <span>{fighter2?.full_name}</span>
      </div>

      {/* Split bar */}
      <div className="prob-bar-wrap">
        <div className="prob-bar-f1" style={{ width: `${f1Pct}%` }}>
          {f1Pct >= 20 && `${f1Pct}%`}
        </div>
        <div className="prob-bar-f2" style={{ width: `${f2Pct}%` }}>
          {f2Pct >= 20 && `${f2Pct}%`}
        </div>
      </div>

      {/* Model probabilities */}
      <div className="prob-pcts">
        <span className="prob-pct-f1">{f1Pct}%</span>
        <span className="prob-pct-f2">{f2Pct}%</span>
      </div>

      {/* Odds row */}
      <div className="odds-row">

        {/* F1 side */}
        <div className="odds-block f1">
          {oddsLoading && <span className="odds-loading">loading odds...</span>}
          {hasOdds && (
            <>
              <span className="odds-american f1">{odds.f1_odds}</span>
              <span className="odds-implied">
                {Math.round(odds.f1_implied * 100)}% implied
              </span>
              {f1Edge && (
                <span className="odds-ev-badge f1">
                  +{f1Edge}% EV
                </span>
              )}
            </>
          )}
        </div>

        {/* Center */}
        <div className="odds-center">
          {hasOdds
            ? <span className="odds-source">
                via {odds.bookmakers} book{odds.bookmakers !== 1 ? 's' : ''}
              </span>
            : !oddsLoading && <span className="odds-na">No odds available</span>
          }
        </div>

        {/* F2 side */}
        <div className="odds-block f2">
          {hasOdds && (
            <>
              {f2Edge && (
                <span className="odds-ev-badge f2">
                  +{f2Edge}% EV
                </span>
              )}
              <span className="odds-implied">
                {Math.round(odds.f2_implied * 100)}% implied
              </span>
              <span className="odds-american f2">{odds.f2_odds}</span>
            </>
          )}
        </div>
      </div>

      {/* Key factors */}
      {prediction.factors?.length > 0 && (
        <>
          <div className="factors-header">KEY FACTORS</div>
          {prediction.factors
            .filter(f => !f.label.startsWith('Style matchup F1#'))
            .map((factor, i) => (
            <div className="factor-row" key={i}>
              <span className="factor-label">{factor.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                  {factor.f1_value} vs {factor.f2_value}
                </span>
                <span className={`factor-adv ${
                  factor.advantage === 'fighter1' ? 'f1' :
                  factor.advantage === 'fighter2' ? 'f2' : 'even'
                }`}>
                  {factor.advantage === 'fighter1'
                    ? fighter1?.full_name?.split(' ').pop() || 'F1'
                    : factor.advantage === 'fighter2'
                    ? fighter2?.full_name?.split(' ').pop() || 'F2'
                    : 'Even'}
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
